import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  MdCameraAlt,
  MdAccessTime,
  MdClose,
  MdOpenInNew,
  MdZoomIn,
} from "react-icons/md";
import type { AlarmItem } from "../types";
import { PERSON_ATTRIBUTE_FIELDS } from "../types";
import {
  buildImageUrl,
  formatUnixTime,
  parseAlarmExtend,
  extractLabel,
  IMAGE_PLACEHOLDER,
} from "../utils";

// ─── ImageModal ───────────────────────────────────────────────────────────────

interface ImageModalProps {
  src: string;
  alt: string;
  item: AlarmItem;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ src, alt, item, onClose }) => {
  const ext = parseAlarmExtend(item.Extend);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);

    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const effectiveSrc = imgError ? IMAGE_PLACEHOLDER : src;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-w-3xl w-full max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <div>
            <p className="text-xs text-gray-400 font-mono">#{item.id}</p>
            <p className="text-sm font-semibold text-gray-800">{item.taskId}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={effectiveSrc}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title="Open full image"
            >
              <MdOpenInNew size={18} />
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <MdClose size={18} />
            </button>
          </div>
        </div>

        <div
          className="bg-gray-950 flex items-center justify-center overflow-hidden shrink-0"
          style={{ maxHeight: "55vh" }}
        >
          <img
            src={effectiveSrc}
            alt={alt}
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: "55vh" }}
            onError={() => setImgError(true)}
          />
        </div>

        <div className="px-4 py-3 overflow-y-auto">
          <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <MdCameraAlt size={13} />
              {item.srcId}
            </span>
            <span className="flex items-center gap-1">
              <MdAccessTime size={13} />
              {formatUnixTime(item.time)}
            </span>
            {ext?.confidence !== undefined && (
              <span className="ml-auto font-mono font-semibold text-indigo-600">
                {(ext.confidence * 100).toFixed(1)}% conf
              </span>
            )}
          </div>

          {ext && (
            <div className="flex flex-wrap gap-1.5">
              {PERSON_ATTRIBUTE_FIELDS.map(({ key, label }) => {
                const rawVal = ext[key];
                const val =
                  typeof rawVal === "string" ? extractLabel(rawVal) : "—";
                if (!val || val === "—") return null;
                return (
                  <span
                    key={key as string}
                    className="inline-flex flex-col items-start bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 gap-0.5"
                  >
                    <span className="text-[9px] uppercase tracking-wider text-gray-400 font-medium leading-none">
                      {label}
                    </span>
                    <span className="text-[11px] font-semibold text-gray-700 capitalize leading-none">
                      {val}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

interface AttributeBadgeProps {
  label: string;
  value: string;
}

const AttributeBadge: React.FC<AttributeBadgeProps> = ({ label, value }) => {
  if (!value || value === "—") return null;
  return (
    <span className="inline-flex flex-col items-start bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 gap-0.5">
      <span className="text-[9px] uppercase tracking-wider text-gray-400 font-medium leading-none">
        {label}
      </span>
      <span className="text-[11px] font-semibold text-gray-700 capitalize leading-none">
        {value}
      </span>
    </span>
  );
};

interface ImagePanelProps {
  bigSrc: string;
  smallSrc: string;
  alt: string;
  onClick: () => void;
}

const ImagePanel: React.FC<ImagePanelProps> = ({ bigSrc, alt, onClick }) => {
  const [bigError, setBigError] = useState(false);
  const effectiveBig = bigError ? IMAGE_PLACEHOLDER : bigSrc;

  return (
    <div
      className="relative w-full rounded-lg overflow-hidden bg-gray-100 border border-gray-100 cursor-zoom-in group/img"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label="View full image"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <img
        src={effectiveBig}
        alt={alt}
        loading="lazy"
        className="relative w-full rounded-lg overflow-hidden bg-gray-100 border border-gray-100 cursor-zoom-in group/img aspect-[4/3]"
        onError={() => setBigError(true)}
      />

      <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors duration-200 flex items-center justify-center">
        <div className="opacity-0 group-hover/img:opacity-100 transition-opacity duration-200 bg-white/90 rounded-full p-1.5 shadow">
          <MdZoomIn size={18} className="text-gray-700" />
        </div>
      </div>
    </div>
  );
};

interface EventCardProps {
  item: AlarmItem;
}

export const EventCard: React.FC<EventCardProps> = ({ item }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const ext = parseAlarmExtend(item.Extend);
  const bigImageUrl = buildImageUrl(item.bigImage);
  const smallImageUrl = buildImageUrl(item.bigImage);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  return (
    <>
      <div className="group bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg hover:border-indigo-100 transition-all duration-200 flex flex-col">
        <div className="p-3 pb-2">
          <ImagePanel
            bigSrc={bigImageUrl}
            smallSrc={smallImageUrl}
            alt={`Event ${item.id}`}
            onClick={openModal}
          />
        </div>

        <div className="px-3 pb-3 flex flex-col gap-2.5 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] text-gray-400 truncate">#{item.id}</p>
              <p
                className="text-sm font-semibold text-gray-800 truncate"
                title={item.taskId}
              >
                {item.taskId || "—"}
              </p>
            </div>
          </div>

          {ext && (
            <div className="flex flex-wrap gap-1.5">
              {PERSON_ATTRIBUTE_FIELDS.map(({ key, label }) => {
                const rawVal = ext[key];
                const val =
                  typeof rawVal === "string" ? extractLabel(rawVal) : "—";
                return (
                  <AttributeBadge
                    key={key as string}
                    label={label}
                    value={val}
                  />
                );
              })}
            </div>
          )}

          <div className="border-t border-gray-50 pt-2 flex items-center justify-between gap-2 flex-wrap mt-auto">
            <div className="flex items-center gap-1 text-[11px] text-gray-400 min-w-0">
              <MdCameraAlt size={12} className="shrink-0" />
              <span className="truncate" title={item.srcId}>
                {item.srcId || "—"}
              </span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-gray-400">
              <MdAccessTime size={12} className="shrink-0" />
              <span>{formatUnixTime(item.time)}</span>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <ImageModal
          src={bigImageUrl}
          alt={`Event ${item.id}`}
          item={item}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default EventCard;
